import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { createCompetitors } from '@/lib/apis';
import { usePostHog } from 'posthog-js/react';

// Maximum URLs per request (rate limiting)
const MAX_URLS_PER_REQUEST = 10;

interface AddCompetitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: number;
  onSuccess?: () => void;
}

const normalizeUrl = (urlString: string): string => {
  const trimmed = urlString.trim();

  // Return empty if string is empty
  if (!trimmed) {
    return '';
  }

  // Add https:// if no protocol is present
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return `https://${trimmed}`;
  }
  return trimmed;
};

const isValidUrl = (urlString: string): boolean => {
  try {
    const normalized = normalizeUrl(urlString);
    const url = new URL(normalized);

    // Check protocol
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }

    // Check hostname exists and is valid
    if (!url.hostname || url.hostname.length === 0) {
      return false;
    }

    // Check hostname doesn't start or end with a dot
    if (url.hostname.startsWith('.') || url.hostname.endsWith('.')) {
      return false;
    }

    // Check hostname has at least one dot (domain.tld format)
    if (!url.hostname.includes('.')) {
      return false;
    }

    // Check hostname has valid characters (alphanumeric, dots, hyphens)
    if (!/^[a-zA-Z0-9.-]+$/.test(url.hostname)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const parseUrls = (text: string): string[] => {
  // Split by newlines, commas, or spaces, trim whitespace, filter empty strings
  return text
    .split(/[\n,\s]+/)
    .map((url) => normalizeUrl(url.trim()))
    .filter((url) => url.length > 0);
};

export const AddCompetitorModal = ({ isOpen, onClose, businessId, onSuccess }: AddCompetitorModalProps) => {
  const [urlsText, setUrlsText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();
  const posthog = usePostHog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!urlsText.trim()) {
      setError('Please enter at least one competitor URL');
      return;
    }

    const urls = parseUrls(urlsText);

    if (urls.length === 0) {
      setError('Please enter at least one competitor URL');
      return;
    }

    // Check URL limit
    if (urls.length > MAX_URLS_PER_REQUEST) {
      setError(`Maximum ${MAX_URLS_PER_REQUEST} URLs per request. You entered ${urls.length} URLs.`);
      return;
    }

    // Validate all URLs
    const invalidUrls = urls.filter((url) => !isValidUrl(url));
    if (invalidUrls.length > 0) {
      setError(`Invalid URL(s): ${invalidUrls.join(', ')}`);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createCompetitors(businessId, urls);

      // Invalidate competitors cache to show placeholder records
      queryClient.invalidateQueries({ queryKey: ['competitors', businessId] });
      // Invalidate task tracking cache to start polling
      queryClient.invalidateQueries({ queryKey: ['competitor-tasks', businessId] });

      // Check for validation errors (SSRF protection rejections)
      if (result.validation_errors && result.validation_errors.length > 0) {
        const errorMessages = result.validation_errors.map(e => `${e.url}: ${e.error}`).join('\n');
        if (result.total_queued === 0) {
          setError(`All URLs were rejected:\n${errorMessages}`);
          return;
        } else {
          setSuccess(`Queued ${result.total_queued} competitor(s). Some URLs were rejected for security reasons.`);
          setTimeout(() => {
            onSuccess?.();
            handleClose();
          }, 2000);
          return;
        }
      }

      // Show appropriate message based on results
      if (result.total_skipped > 0 && result.total_queued === 0) {
        setError(`All URLs were already being processed or exist as competitors.`);
      } else if (result.total_skipped > 0) {
        posthog?.capture('competitor_added', {
          business_id: businessId,
          urls_submitted: urls.length,
          urls_queued: result.total_queued,
          urls_skipped: result.total_skipped,
        });
        setSuccess(`Queued ${result.total_queued} competitor(s) for analysis. ${result.total_skipped} were skipped (already exist).`);
        // Close after a short delay to show the message
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 1500);
      } else {
        // All succeeded - close modal immediately
        posthog?.capture('competitor_added', {
          business_id: businessId,
          urls_submitted: urls.length,
          urls_queued: result.total_queued,
          urls_skipped: 0,
        });
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      console.error('Error adding competitors:', err);
      posthog?.capture('competitor_add_failed', {
        business_id: businessId,
        urls_attempted: urls.length,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      setError(err instanceof Error ? err.message : 'Failed to add competitors. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setUrlsText('');
    setError('');
    setSuccess('');
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-lg font-semibold text-gray-900">Add Competitors</DialogTitle>
          <DialogDescription className="text-sm text-gray-700">
            Enter competitor websites (one per line or space-separated) to analyze their business and include them in competitor campaigns. Maximum {MAX_URLS_PER_REQUEST} URLs per submission.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 pb-6">
          <div className="space-y-2">
            <Textarea
              id="competitor-urls"
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder="E.g. www.glitchads.ai"
              className="min-h-[200px] text-gray-900"
              disabled={isSubmitting}
            />
          </div>

          {success && (
            <div className="border-l-4 border-l-green-400 border-t border-r border-b border-green-400 rounded-lg bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900">{success}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="border-l-4 border-l-red-400 border-t border-r border-b border-red-400 rounded-lg bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900">{error}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center space-x-2"
            >
              <span>{isSubmitting ? 'Adding...' : 'Add Competitors'}</span>
              {!isSubmitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
